import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

import { cn } from '@shared/utils';

dayjs.locale('pt-br');

export interface DateTimeInputProps {
  readonly id?: string;
  readonly label?: string;
  /** Current value as an ISO datetime string (or empty when unset). */
  readonly value?: string;
  /** Called with the new ISO datetime string, or an empty string when cleared. */
  readonly onChange?: (value: string) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly className?: string;
}

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DEFAULT_TIME = '00:00';

function formatDisplay(value: string | undefined): string {
  if (value === undefined || value === '') return '';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : '';
}

function getDayClass(isSelected: boolean, isToday: boolean): string {
  if (isSelected) return 'bg-[#003B6A] text-white';
  if (isToday) return 'border border-[#003B6A] text-[#003B6A] hover:bg-[#003B6A]/10';
  return 'text-login-text-secondary hover:bg-[#F3F4F6]';
}

/**
 * Combined date + time picker in a single input, following the `DateRangePicker` layout: a trigger
 * button (calendar icon) that opens a popup with a month calendar plus a time field. Emits a
 * single ISO datetime string via `onChange`. Use it instead of composing `DateInput` +
 * `<input type="time">` so the field matches the form standard height/shape.
 */
export function DateTimeInput({
  id,
  label,
  value,
  onChange,
  placeholder = 'Selecione data e hora',
  disabled,
  invalid,
  className,
}: DateTimeInputProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => (value ? dayjs(value) : dayjs()));
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync calendar view with external value
    if (value) setViewDate(dayjs(value));
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = value ? dayjs(value) : null;
  const timeValue = selected?.isValid() === true ? selected.format('HH:mm') : '';

  const selectDate = useCallback(
    (date: dayjs.Dayjs) => {
      const time = selected?.isValid() === true ? selected.format('HH:mm') : DEFAULT_TIME;
      const [h, m] = time.split(':');
      const next = date.hour(Number(h)).minute(Number(m)).second(0).millisecond(0);
      onChange?.(next.toISOString());
    },
    [onChange, selected],
  );

  const changeTime = useCallback(
    (time: string) => {
      if (time === '') return;
      const [h, m] = time.split(':');
      const base = selected?.isValid() === true ? selected : dayjs();
      const next = base.hour(Number(h)).minute(Number(m)).second(0).millisecond(0);
      onChange?.(next.toISOString());
    },
    [onChange, selected],
  );

  const prevMonth = (): void => setViewDate(viewDate.subtract(1, 'month'));
  const nextMonth = (): void => setViewDate(viewDate.add(1, 'month'));

  const daysInMonth = viewDate.daysInMonth();
  const firstDayOfWeek = viewDate.startOf('month').day();
  const days: (dayjs.Dayjs | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(viewDate.date(d));

  return (
    <div ref={containerRef} className={cn('relative flex flex-col gap-1.5', className)}>
      {label !== undefined ? (
        <label htmlFor={id} className="text-xs font-semibold text-login-text-secondary">
          {label}
        </label>
      ) : null}

      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-invalid={invalid === true ? 'true' : undefined}
        onClick={() => {
          if (!disabled) setIsOpen(!isOpen);
        }}
        className={cn(
          // Form-standard field height/shape (h-14, rounded-sm), aligned with the other inputs.
          'flex h-14 w-full items-center justify-between rounded-sm border bg-white px-3',
          'text-sm text-login-text-secondary transition-colors',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-60',
          invalid === true
            ? 'border-2 border-login-border-error'
            : 'border-login-border-primary hover:border-login-border-brand',
        )}
      >
        <span className={value ? '' : 'text-[#A4A7AE]'}>{formatDisplay(value) || placeholder}</span>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-[#71767F]"
          aria-hidden="true"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>

      {isOpen ? (
        <div className="absolute top-full right-0 z-50 mt-1 w-72 rounded-lg border border-login-border-primary bg-white p-4 shadow-lg">
          {/* Month navigation */}
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={prevMonth}
              className="flex h-7 w-7 items-center justify-center rounded-md text-login-text-secondary hover:bg-[#F3F4F6]"
              aria-label="Mês anterior"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-login-text-secondary capitalize">
              {viewDate.format('MMMM YYYY')}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              className="flex h-7 w-7 items-center justify-center rounded-md text-login-text-secondary hover:bg-[#F3F4F6]"
              aria-label="Próximo mês"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-0.5 text-center">
            {WEEKDAYS.map((day) => (
              <span key={day} className="py-1 text-[10px] font-semibold text-[#71767F]">
                {day}
              </span>
            ))}
            {days.map((day, idx) => {
              if (day === null) return <span key={`empty-${String(idx)}`} />;
              const dateStr = day.format('YYYY-MM-DD');
              const isSelected = selected !== null && day.isSame(selected, 'day');
              const isToday = day.isSame(dayjs(), 'day');
              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => selectDate(day)}
                  className={cn(
                    'h-8 w-8 rounded-md text-xs font-medium transition-colors',
                    getDayClass(isSelected, isToday),
                  )}
                >
                  {day.date()}
                </button>
              );
            })}
          </div>

          {/* Time field */}
          <div className="mt-3 flex items-center justify-between border-t border-[#F3F4F6] pt-3">
            <label
              htmlFor={`${id ?? 'datetime'}-time`}
              className="text-xs font-semibold text-login-text-secondary"
            >
              Hora
            </label>
            <input
              id={`${id ?? 'datetime'}-time`}
              type="time"
              value={timeValue}
              onChange={(e) => changeTime(e.target.value)}
              className={cn(
                'h-9 rounded-md border border-login-border-primary bg-white px-2 text-sm text-login-text-secondary',
                'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1',
              )}
              aria-label="Hora"
            />
          </div>

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="h-8 rounded-full bg-login-btn-primary px-4 text-xs font-semibold text-login-text-secondary hover:opacity-90"
            >
              Confirmar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
